import React, { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { API_URLS, BASE_URL } from "../../../api/urls";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { DeveloperTokenManager } from "../../../utils/developerTokenManager";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";

// ✅ UPDATED: Added "feedback_reshared" type
interface ApiNotification {
  id: number;
  title: string;
  message: string;
  performed_by: string | null; // ✅ FIXED: Allow null
  performed_by_image: string | null;
  button_label: string;
  type: "card_shared" | "developer_action" | "feedback_reshared";
  feedback_id: number;
  card_id: number;
  workspace_id: number;
  is_read: boolean;
  created_at: string;
}

// ✅ UPDATED: Added "feedback_reshared" type
interface NotificationItem {
  id: number;
  type: "card_shared" | "developer_action" | "feedback_reshared";
  userName: string;
  userAvatar: string;
  performerImage: string | null;
  message: string;
  timestamp: string;
  isUnread: boolean;
  actionUrl?: string;
  actionLabel?: string;
  feedbackId: number;
  cardId: number;
  workspaceId: number;
  cardData?: {
    name: string;
    feedbackCount: number;
    previewImage: string;
    createdAt: string;
  };
  isLoadingCardData?: boolean;
}

interface NotificationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onNotificationCountChange?: (count: number) => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({
  isOpen,
  onClose,
  onNotificationCountChange,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openingCard, setOpeningCard] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const processedReshareNotifications = new Set<number>();

  // Animation handling
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isAnimating]);

  // ✅ NEW: Fetch full card data before showing notification
  const fetchCardDataForNotification = async (
    cardId: number,
    workspaceId: number,
    isReshareEvent: boolean = false,
  ): Promise<any> => {
    try {
      console.log(`🔍 Pre-fetching FULL card data for Card ID: ${cardId}`, {
        isReshareEvent,
      });

      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        console.error("❌ No access token");
        return null;
      }

      const currentUserEmail = EnhancedTokenManager.getCurrentUserEmail();
      if (!currentUserEmail) {
        console.error("❌ No user email");
        return null;
      }

      const developerToken =
        await DeveloperTokenManager.getDeveloperTokenForEmail(currentUserEmail);
      if (!developerToken) {
        console.error("❌ No developer token");
        return null;
      }

      const response = await fetch(API_URLS.VIEW_FEEDBACK_BY_DEVELOPER, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${developerToken}`,
          "Content-Type": "application/json",
          "X-Developer-Email": currentUserEmail,
        },
      });

      if (!response.ok) {
        console.error(`❌ Failed to fetch developer cards: ${response.status}`);
        return null;
      }

      const allCards = await response.json();
      console.log(`📦 Fetched ${allCards.length} developer cards`);

      const targetCard = allCards.find((c: any) => c.card_id === cardId);

      if (!targetCard) {
        console.warn(`⚠️ Card ${cardId} not found in developer cards`);
        return null;
      }

      console.log(`✅ Found target card:`, {
        cardId: targetCard.card_id,
        name: targetCard.card_name,
        feedbackCount: targetCard.feedbacks?.length || 0,
      });

      let previewImage = null;
      if (targetCard.feedbacks && targetCard.feedbacks.length > 0) {
        const firstWithImage = targetCard.feedbacks.find(
          (f: any) => f.screenshot_url,
        );
        if (firstWithImage?.screenshot_url) {
          const imagePath = firstWithImage.screenshot_url;
          if (imagePath.startsWith("data:image/")) {
            previewImage = imagePath;
          } else if (imagePath.startsWith("http")) {
            previewImage = imagePath;
          } else if (imagePath.startsWith("/")) {
            previewImage = `${BASE_URL}${imagePath}`;
          } else {
            previewImage = `${BASE_URL}/${imagePath}`;
          }
        }
      }

      const developersResponse = await fetch(
        API_URLS.GET_DEVELOPER_CARD_SENDERS_LIST(cardId),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      let developers = [];
      if (developersResponse.ok) {
        const devData = await developersResponse.json();
        developers =
          devData.success && devData.developers ? devData.developers : [];
        console.log(
          `✅ Fetched ${developers.length} developers for card ${cardId}`,
        );
      }

      const result = {
        name: targetCard.card_name,
        feedbackCount: targetCard.feedbacks?.length || 0,
        previewImage: previewImage || null,
        createdAt:
          targetCard.feedbacks?.[0]?.created_at || new Date().toISOString(),
        fullFeedbacks: targetCard.feedbacks || [],
        developers: developers,
        isReshared: isReshareEvent,
      };

      console.log(`✅ Complete card data prepared:`, {
        name: result.name,
        feedbackCount: result.feedbackCount,
        hasFeedbacks: result.fullFeedbacks.length > 0,
        hasDevelopers: result.developers.length > 0,
      });

      return result;
    } catch (error) {
      console.error("❌ Error fetching card:", error);
      return null;
    }
  };

  // ✅ FIXED: Handle null performed_by
  const transformNotification = async (
    apiNotif: ApiNotification,
  ): Promise<NotificationItem> => {
    const getInitials = (name: string | null): string => {
      if (!name) return "??";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    const getFullImageUrl = (imagePath: string | null): string | null => {
      if (!imagePath) return null;
      if (imagePath.startsWith("http")) return imagePath;
      const cleanPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");
      return `${BASE_URL}/${cleanPath}`;
    };

    let actionLabel = "View feedback";
    if (
      apiNotif.type === "card_shared" ||
      apiNotif.type === "feedback_reshared"
    ) {
      actionLabel = "View feedback";
    } else if (apiNotif.type === "developer_action") {
      actionLabel = apiNotif.message.toLowerCase().includes("unclear")
        ? "Unclear feedback"
        : "Acknowledged feedback";
    }

    let cardData = null;
    if (
      apiNotif.type === "card_shared" ||
      apiNotif.type === "feedback_reshared"
    ) {
      console.log(
        `🚀 Pre-fetching FULL card data for notification ${apiNotif.id}...`,
      );

      const isReshareEvent = apiNotif.type === "feedback_reshared";

      cardData = await fetchCardDataForNotification(
        apiNotif.card_id,
        apiNotif.workspace_id,
        isReshareEvent,
      );

      if (cardData) {
        console.log(
          `✅ Complete card data ready for notification ${apiNotif.id}:`,
          {
            name: cardData.name,
            feedbackCount: cardData.feedbackCount,
            hasFeedbacks: cardData.fullFeedbacks?.length > 0,
            hasDevelopers: cardData.developers?.length > 0,
          },
        );

        // ✅ FIX: Store BOTH card creation time AND notification share time
        sessionStorage.setItem(
          `instant_card_${apiNotif.card_id}`,
          JSON.stringify({
            cardId: apiNotif.card_id,
            cardName: cardData.name,
            feedbackCount: cardData.feedbackCount,
            previewImage: cardData.previewImage,

            sharedAt: apiNotif.created_at, // ✅ NEW: Notification creation time (share time - just now!)
            fullFeedbacks: cardData.fullFeedbacks,
            developers: cardData.developers,
            instant: true,
            isReshared: cardData.isReshared,
          }),
        );

        // ✅ NEW: Enhanced logging to show both timestamps
        console.log(`✅ Stored card with timestamps:`, {
          cardId: apiNotif.card_id,
          cardCreatedAt: cardData.createdAt, // When project was created
          notificationCreatedAt: apiNotif.created_at, // When notification was created (SHARE TIME!)
          explanation: 'Using notificationCreatedAt for "shared at" display',
        });

        if (isReshareEvent) {
          localStorage.setItem(`card_${apiNotif.card_id}_reshared`, "true");
          console.log(
            `✅ Marked card ${apiNotif.card_id} as reshared in localStorage`,
          );
        }

        console.log(
          `✅ Complete card ${apiNotif.card_id} stored in sessionStorage with feedbacks and developers`,
        );

        window.dispatchEvent(
          new CustomEvent("card-processed-notification", {
            detail: { cardId: apiNotif.card_id },
          }),
        );

        console.log(
          `✅ Notified DashboardScreen that card ${apiNotif.card_id} is ready for display`,
        );
      }
    }

    const displayMessage =
      apiNotif.type === "developer_action"
        ? "Developer has reacted to your feedback"
        : apiNotif.title;

    return {
      id: apiNotif.id,
      type: apiNotif.type,
      userName: apiNotif.performed_by || "Unknown User",
      userAvatar: getInitials(apiNotif.performed_by),
      performerImage: getFullImageUrl(apiNotif.performed_by_image),
      message: displayMessage,
      timestamp: apiNotif.created_at,
      isUnread: !apiNotif.is_read,
      actionUrl: `/feedback/${apiNotif.card_id}`,
      actionLabel,
      feedbackId: apiNotif.feedback_id,
      cardId: apiNotif.card_id,
      workspaceId: apiNotif.workspace_id,
      cardData: cardData || undefined,
      isLoadingCardData: false,
    };
  };

  // ✅ UPDATED: Fetch notifications with retry support
  const fetchNotifications = useCallback(
    async (isRetry: boolean = false) => {
      try {
        // ✅ Clear error state when retrying
        if (isRetry) {
          setError(null);
          setLoading(true);
        }

        const tokenValid = await EnhancedTokenManager.ensureValidToken();

        if (!tokenValid) {
          console.warn("⚠️ Token refresh failed");
          if (isRetry) {
            setLoading(false);
            setError("Authentication failed. Please try again.");
          }
          return;
        }

        const response = await EnhancedTokenManager.fetchWithAutoRefresh(
          API_URLS.GET_NOTIFICATIONS,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          if (isOpen || isRetry) {
            throw new Error(
              `Failed to fetch notifications: ${response.status}`,
            );
          }
          return;
        }

        const data = await response.json();

        if (data.success && data.notifications) {
          // ✅ FIXED: Filter out notifications that should be hidden
          const unreadNotifs = data.notifications.filter(
            (notif: ApiNotification) => {
              // Filter out read notifications
              if (notif.is_read) return false;

              // ✅ Filter out already processed reshare notifications
              if (
                notif.type === "feedback_reshared" &&
                processedReshareNotifications.has(notif.id)
              ) {
                console.log(
                  `⏭️ Skipping already processed reshare notification ${notif.id}`,
                );
                return false;
              }

              return true;
            },
          );

          console.log(
            `📥 Processing ${unreadNotifs.length} unread notifications...`,
          );

          const transformedNotifications: NotificationItem[] = [];

          for (const notif of unreadNotifs) {
            const transformed = await transformNotification(notif);
            transformedNotifications.push(transformed);
          }

          console.log(
            `✅ All ${transformedNotifications.length} notifications processed`,
          );

          setNotifications(transformedNotifications);

          // Dispatch event for notification count update
          window.dispatchEvent(
            new CustomEvent("new-notification-received", {
              detail: { count: transformedNotifications.length },
            }),
          );

          if (onNotificationCountChange) {
            onNotificationCountChange(transformedNotifications.length);
          }

          // ✅ Clear loading state after successful retry
          if (isRetry) {
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("❌ Error fetching notifications:", err);
        if (isOpen || isRetry) {
          setError(
            err instanceof Error ? err.message : "Failed to load notifications",
          );
          setLoading(false);
        }
      }
    },
    [isOpen, onNotificationCountChange],
  );

  // ✅ NEW: Handle retry button click
  const handleRetry = () => {
    console.log("🔄 Retry button clicked - refreshing notifications");
    fetchNotifications(true);
  };

  // Initial fetch and polling
  useEffect(() => {
    if (isOpen && !initialDataLoaded) {
      setLoading(true);
    }

    fetchNotifications().finally(() => {
      setInitialDataLoaded(true);
      if (isOpen) {
        setLoading(false);
      }
    });

    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  // Mark notification as read
  const markNotificationAsRead = async (notificationId: number) => {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        console.error(
          "❌ No access token available for marking notification as read",
        );
        return false;
      }

      console.log(`📤 Marking notification ${notificationId} as read...`);

      const response = await fetch(
        API_URLS.MARK_NOTIFICATIONS_AS_READ(notificationId),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ Failed to mark notification ${notificationId} as read:`,
          response.status,
          errorText,
        );
        return false;
      }

      const data = await response.json();

      if (data.success) {
        console.log(
          `✅ Notification ${notificationId} marked as read successfully`,
        );

        // ✅ CRITICAL: Remove from UI immediately using functional update
        setNotifications((prev) => {
          const newNotifications = prev.filter((n) => n.id !== notificationId);

          // Update parent component with new count
          if (onNotificationCountChange) {
            onNotificationCountChange(newNotifications.length);
          }

          console.log(
            `✅ Notification ${notificationId} removed from UI. Remaining: ${newNotifications.length}`,
          );
          return newNotifications;
        });

        return true;
      } else {
        console.error(
          `❌ Server returned success: false for notification ${notificationId}`,
        );
        return false;
      }
    } catch (err) {
      console.error(
        `❌ Error marking notification ${notificationId} as read:`,
        err,
      );
      return false;
    }
  };

  useEffect(() => {
    const handleNotificationsRead = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId, count } = event.detail;

      console.log(`🔔 ${count} notifications dismissed for card ${cardId}`);

      // Remove notifications from UI
      setNotifications((prev) => prev.filter((n) => n.cardId !== cardId));

      // Update parent count
      if (onNotificationCountChange) {
        const newCount = notifications.length - count;
        onNotificationCountChange(Math.max(0, newCount));
      }
    };

    window.addEventListener(
      "notifications-marked-read",
      handleNotificationsRead as EventListener,
    );

    return () => {
      window.removeEventListener(
        "notifications-marked-read",
        handleNotificationsRead as EventListener,
      );
    };
  }, [notifications.length, onNotificationCountChange]);

  // ✅ Handle action click for all notification types
  const handleActionClick = async (
    notification: NotificationItem,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    console.log("⚡ Notification clicked:", {
      type: notification.type,
      cardId: notification.cardId,
      feedbackId: notification.feedbackId,
      notificationId: notification.id,
    });

    // ✅ CRITICAL: Mark notification as read IMMEDIATELY for ALL types
    // This ensures the notification is removed from the list
    await markNotificationAsRead(notification.id);

    if (notification.cardId) {
      const viewedCards = JSON.parse(
        localStorage.getItem("viewed_developer_cards") || "[]",
      );
      if (!viewedCards.includes(notification.cardId)) {
        viewedCards.push(notification.cardId);
        localStorage.setItem(
          "viewed_developer_cards",
          JSON.stringify(viewedCards),
        );
      }
    }

    // ✅ FIXED: Handle developer_action notifications (acknowledged/unclear)
    if (notification.type === "developer_action" && notification.cardId) {
      console.log("🎯 Developer action notification:", {
        action: notification.message,
        feedbackId: notification.feedbackId,
      });

      // Set highlight flags
      localStorage.setItem(
        "highlight_feedback_id",
        notification.feedbackId.toString(),
      );
      localStorage.setItem("developer_action", notification.message);

      // Dispatch event to open the card in "Created" tab
      const eventName = "open-created-card-from-notification";
      const eventDetail = {
        cardId: notification.cardId,
        feedbackId: notification.feedbackId,
        workspaceId: notification.workspaceId,
        action: notification.message,
        cardData: notification.cardData,
        instant: !!notification.cardData,
        notificationId: notification.id,
        actionBy: notification.userName,
        actionAt: notification.timestamp,
      };

      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: eventDetail,
          bubbles: true,
          composed: true,
        }),
      );

      // Close notification popup after dispatching event
      setTimeout(() => {
        onClose();
      }, 100);

      console.log(
        `✅ Developer action notification processed and marked as read: ${notification.id}`,
      );
    }
    // ✅ Handle card_shared and feedback_reshared notifications
    else if (
      notification.type === "card_shared" ||
      notification.type === "feedback_reshared"
    ) {
      // ✅ FIXED: Handle reshare notifications differently
      if (notification.type === "feedback_reshared") {
        localStorage.setItem(
          "highlight_feedback_id",
          notification.feedbackId.toString(),
        );
        localStorage.setItem("feedback_reshare_action", "reshared");
        localStorage.setItem(`card_${notification.cardId}_reshared`, "true");

        console.log("🔄 Reshare notification - triggering background refresh");

        processedReshareNotifications.add(notification.id);

        // Trigger silent refresh in background
        window.dispatchEvent(
          new CustomEvent("refresh-card-from-reshare", {
            detail: {
              cardId: notification.cardId,
              feedbackId: notification.feedbackId,
              notificationId: notification.id,
              sharedBy: notification.userName,
              sharedAt: notification.timestamp,
              isReshared: true,
            },
          }),
        );

        onClose();
        return;
      }

      // Handle regular card_shared
      const eventName = "open-shared-card-from-notification";
      const eventDetail = {
        cardId: notification.cardId,
        feedbackId: notification.feedbackId,
        workspaceId: notification.workspaceId,
        cardData: notification.cardData,
        instant: !!notification.cardData,
        notificationId: notification.id,
        sharedBy: notification.userName,
        sharedAt: notification.timestamp,
        shouldHighlightFeedback: false,
        isReshareEvent: false,
      };

      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: eventDetail,
          bubbles: true,
          composed: true,
        }),
      );

      setTimeout(() => {
        onClose();
      }, 100);

      console.log(
        `✅ Card shared notification processed and marked as read: ${notification.id}`,
      );
    }
  };

  // Time formatting utilities
  const convertToIST = (timestamp: string): Date => {
    return new Date(timestamp + "Z");
  };

  const getTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const date = convertToIST(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  };

  const formatTime = (timestamp: string): string => {
    const date = convertToIST(timestamp);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  };

  const isToday = (timestamp: string): boolean => {
    const today = new Date();
    const date = convertToIST(timestamp);
    const todayIST = new Date(
      today.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
    );
    const dateIST = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
    );

    return (
      dateIST.getDate() === todayIST.getDate() &&
      dateIST.getMonth() === todayIST.getMonth() &&
      dateIST.getFullYear() === todayIST.getFullYear()
    );
  };

  const todayNotifications = notifications.filter((n) => isToday(n.timestamp));
  const olderNotifications = notifications.filter((n) => !isToday(n.timestamp));
  const totalUnreadCount = notifications.length;

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-500 ease-in-out ${
          isOpen ? "bg-opacity-25" : "bg-opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed right-0 top-0 h-full w-[420px] 3xl:w-[538px] bg-white shadow-2xl z-50 transition-transform duration-500 ease-out ${
          isOpen && isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-200">
          <h2
            className="text-[16px] 3xl:text-[18px] font-semibold text-[#1E1F26]"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            All notifications ({totalUnreadCount})
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-80px)] custom-scrollbar-overlay">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full px-6">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
              <p
                className="text-gray-600 font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Loading notifications...
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p
                className="text-gray-600 font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Failed to load notifications
              </p>
              <p
                className="text-sm text-gray-500 mt-1"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {error}
              </p>
              <button
                onClick={handleRetry}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Retry
              </button>
            </div>
          ) : totalUnreadCount === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </div>
              <p
                className="text-gray-600 font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                No notifications yet
              </p>
              <p
                className="text-sm text-gray-500 mt-1"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                You'll see notifications here when there's activity
              </p>
            </div>
          ) : (
            <>
              {/* Today's Notifications */}
              {todayNotifications.length > 0 && (
                <div>
                  <div className="px-6 py-3 bg-white sticky top-0 z-10">
                    <div className="flex items-center justify-between">
                      <h3
                        className="text-[12px] 2xl:text-[14px] font-medium text-[#1E1F26]"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Today
                      </h3>
                      <span
                        className="text-[10px] 2xl:text-[12px] text-[#61646F] font-medium"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        {todayNotifications.length} Unread
                      </span>
                    </div>
                  </div>
                  <div className="px-3 py-2 space-y-3">
                    {todayNotifications.map((notification) => (
                      <div
                        key={notification.id}
                        className="px-4 py-2 rounded-xl transition-all bg-[#F8F9FC] shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {notification.performerImage ? (
                              <img
                                src={notification.performerImage}
                                alt={notification.userName}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback = e.currentTarget
                                    .nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm"
                              style={{
                                display: notification.performerImage
                                  ? "none"
                                  : "flex",
                              }}
                            >
                              {notification.userAvatar}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p
                                  className="text-sm font-semibold text-gray-900"
                                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                >
                                  {notification.userName}
                                </p>
                                <p
                                  className="text-sm text-gray-600 mt-0.5"
                                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                >
                                  {notification.message}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className="text-xs text-gray-500 whitespace-nowrap"
                                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                >
                                  {formatTime(notification.timestamp)}
                                </span>
                                {notification.isUnread && (
                                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                                )}
                              </div>
                            </div>

                            {notification.actionLabel && (
                              <button
                                onClick={(e) =>
                                  handleActionClick(notification, e)
                                }
                                disabled={
                                  openingCard &&
                                  (notification.type === "card_shared" ||
                                    notification.type === "feedback_reshared")
                                }
                                className={`mt-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2 ${
                                  notification.type === "card_shared" ||
                                  notification.type === "feedback_reshared"
                                    ? "bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    : notification.actionLabel ===
                                        "Unclear feedback"
                                      ? "border border-orange-500 text-orange-600 hover:bg-orange-50"
                                      : "border border-green-500 text-green-600 hover:bg-green-50"
                                }`}
                                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                              >
                                {openingCard &&
                                (notification.type === "card_shared" ||
                                  notification.type === "feedback_reshared") ? (
                                  <>
                                    <svg
                                      className="animate-spin h-4 w-4"
                                      style={{
                                        fontFamily: DEFAULT_FONT_FAMILY,
                                      }}
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                    Opening...
                                  </>
                                ) : (
                                  <>
                                    {notification.actionLabel}
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d={
                                          notification.type === "card_shared" ||
                                          notification.type ===
                                            "feedback_reshared"
                                            ? "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            : "M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        }
                                      />
                                    </svg>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Older Notifications */}
              {olderNotifications.length > 0 && (
                <div>
                  <div className="px-6 py-3 bg-white sticky top-0 z-10">
                    <div className="flex items-center justify-between">
                      <h3
                        className="text-[12px] 2xl:text-[14px] font-medium text-[#1E1F26]"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        Older
                      </h3>
                      {olderNotifications.length > 0 && (
                        <span
                          className="text-[10px] 2xl:text-[12px] text-[#61646F] font-medium"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          {olderNotifications.length} Unread
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-3 py-2 space-y-3">
                    {olderNotifications.map((notification) => (
                      <div
                        key={notification.id}
                        className="px-4 py-2 rounded-xl transition-all bg-[#F8F9FC] shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {notification.performerImage ? (
                              <img
                                src={notification.performerImage}
                                alt={notification.userName}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback = e.currentTarget
                                    .nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm"
                              style={{
                                display: notification.performerImage
                                  ? "none"
                                  : "flex",
                              }}
                            >
                              {notification.userAvatar}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="flex-1">
                                <p
                                  className="text-sm font-semibold text-gray-900"
                                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                >
                                  {notification.userName}
                                </p>
                                <p
                                  className="text-sm text-gray-600 mt-0.5"
                                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                >
                                  {notification.message}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className="text-xs text-gray-500 whitespace-nowrap"
                                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                >
                                  {getTimeAgo(notification.timestamp)}
                                </span>
                                {notification.isUnread && (
                                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                                )}
                              </div>
                            </div>

                            {notification.actionLabel && (
                              <button
                                onClick={(e) =>
                                  handleActionClick(notification, e)
                                }
                                disabled={
                                  openingCard &&
                                  (notification.type === "card_shared" ||
                                    notification.type === "feedback_reshared")
                                }
                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2 ${
                                  notification.type === "card_shared" ||
                                  notification.type === "feedback_reshared"
                                    ? "bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    : notification.actionLabel ===
                                        "Unclear feedback"
                                      ? "border border-orange-500 text-orange-600 hover:bg-orange-50"
                                      : "border border-green-500 text-green-600 hover:bg-green-50"
                                }`}
                                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                              >
                                {openingCard &&
                                (notification.type === "card_shared" ||
                                  notification.type === "feedback_reshared") ? (
                                  <>
                                    <svg
                                      className="animate-spin h-4 w-4"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                    Opening...
                                  </>
                                ) : (
                                  <>
                                    {notification.actionLabel}
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d={
                                          notification.type === "card_shared" ||
                                          notification.type ===
                                            "feedback_reshared"
                                            ? "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            : "M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        }
                                      />
                                    </svg>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPopup;
